import { useState } from 'react';
import styles from './CopyButton.module.css';

export interface CopyButtonProps {
  /** The value to copy to clipboard. */
  value: string;
  /** Optional label for accessibility (defaults to "Copy"). Used in aria-label. */
  label?: string;
  /** Optional className for styling overrides. */
  className?: string;
}

/**
 * C-6 (walkthrough 2026-05-11). Small reusable click-to-copy button.
 * Uses navigator.clipboard.writeText; shows "Copied" for 1.5s after a
 * successful copy. Falls back to a console warning if clipboard is
 * unavailable (e.g. non-secure context).
 */
export function CopyButton({ value, label = 'Copy', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleClick() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn('[capture] clipboard.writeText failed:', err);
    }
  }

  return (
    <button
      type="button"
      className={`${styles.copyButton}${className ? ' ' + className : ''}`}
      onClick={handleClick}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
    >
      {copied ? '✓ Copied' : '⧉ Copy'}
    </button>
  );
}
